import Vue from 'vue';
import permitFieldService from '@/api/permit-field-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';

const getDefaultPermitFieldFormItem = () => ({
  field_type: 'text',
  is_required: false,
  is_visible: false,
});

export const getDefaultPermitFieldFilterParams = () => ({});

const state = {
  permitFields: null,
  editedPermitField: {},
  permitFieldValidationErrors: {},
  permitFieldFilterParams: getDefaultPermitFieldFilterParams(),
  newPermitField: getDefaultPermitFieldFormItem(),
};

const getters = {
  permitFieldTypes() {
    return [
      {
        label: i18n.t('permit_field_types.text'),
        value: 'text',
      },
      {
        label: i18n.t('permit_field_types.numeric'),
        value: 'numeric',
      },
      {
        label: i18n.t('permit_field_types.date'),
        value: 'date',
      },
    ];
  },
};

const mutations = {
  SET_PERMIT_FIELDS(state, fields) {
    state.permitFields = fields;
  },

  SET_PERMIT_FIELD_FILTER_PARAMS(state, params) {
    state.permitFieldFilterParams = params;
  },

  SET_EDITED_PERMIT_FIELD(state, permitField) {
    state.permitFieldValidationErrors = {};
    state.editedPermitField = JSON.parse(JSON.stringify(permitField));
  },

  STORE_PERMIT_FIELD(state, permitField) {
    state.permitFields = [...state.permitFields, permitField];
    state.permitFieldValidationErrors = {};
    state.newPermitField = getDefaultPermitFieldFormItem();
  },

  UPDATE_PERMIT_FIELD(state, permitField) {
    state.permitFields = updateArrayItem(state.permitFields, permitField);
  },

  DELETE_PERMIT_FIELD(state, permitField) {
    state.permitFields = removeArrayItem(state.permitFields, permitField);
  },

  SET_PERMIT_FIELD_VALIDATION_ERRORS(state, permitFieldValidationErrors) {
    state.permitFieldValidationErrors = permitFieldValidationErrors;
  },

  RESET_PERMIT_FIELD_VALIDATION_ERRORS(state) {
    state.permitFieldValidationErrors = {};
  },

  CLEAR_PERMIT_FIELD_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.permitFieldValidationErrors, field);
  },
};

const actions = {
  fetchPermitFields({ commit }, params) {
    commit('SET_PERMIT_FIELD_FILTER_PARAMS', params);
    return permitFieldService.getPage(params).then((res) => {
      commit('SET_PERMIT_FIELDS', res.data);
      return res.data;
    });
  },

  storePermitField({ commit }, permitField) {
    return permitFieldService
      .create(permitField)
      .then((res) => {
        commit('STORE_PERMIT_FIELD', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_field_created'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_FIELD_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editPermitField({ state, commit }, permitFieldId) {
    const permitField = state.permitFields?.find((c) => c.id === permitFieldId);
    if (permitField) {
      commit('SET_EDITED_PERMIT_FIELD', permitField);
      return Promise.resolve(permitField);
    }
    return permitFieldService.getById(permitFieldId).then((res) => {
      commit('SET_EDITED_PERMIT_FIELD', res.data);
      return res.data;
    });
  },

  updatePermitField({ commit }, permitField) {
    return permitFieldService
      .update(permitField)
      .then((res) => {
        commit('UPDATE_PERMIT_FIELD', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_field_updated'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_FIELD_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  clearPermitFieldValidationErrors({ commit }, field) {
    commit('CLEAR_PERMIT_FIELD_VALIDATION_ERRORS', field);
  },

  deletePermitField({ commit }, permitField) {
    openConfirmDialog({
      title: i18n.t('confirm_permit_field_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      permitFieldService.delete(permitField).then(() => {
        commit('DELETE_PERMIT_FIELD', permitField);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_field_deleted'));
      });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
